import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { formatFootageNumber } from "./expansionProgressUtils";

function formatReportDate(value) {
  if (value == null || String(value).trim() === "") return "—";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }
  return raw;
}

function StatRow({ label, value }) {
  return (
    <div className="inspector-report-stat-row">
      <span className="inspector-report-stat-label">{label}</span>
      <span className="inspector-report-stat-value">{value}</span>
    </div>
  );
}

/**
 * Read-only popup when a user clicks an inspector report calendar chip.
 */
export default function InspectorReportCalendarModal({ event, projectRow, onClose }) {
  useEffect(() => {
    if (!event) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [event, onClose]);

  if (!event || !projectRow) return null;

  const report = event.report || {};
  const segment = event.segment || "—";
  const workOrder = event.workOrder ?? "—";
  const customer = event.customer ?? "—";
  const reportedBy = report.reported_by != null ? String(report.reported_by).trim() : "";
  const contractor = report.contractor != null ? String(report.contractor).trim() : "";
  const notes = report.notes != null ? String(report.notes).trim() : "";

  return (
    <div
      className="schedule-calendar-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="schedule-calendar-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inspector-report-calendar-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="inspector-report-calendar-title" className="schedule-calendar-modal-title">
          {event.eventType === "crewReports" ? "Crew report" : "Inspector report"}
        </h2>
        <p className="schedule-calendar-modal-meta">
          <strong>Segment {segment}</strong> · {workOrder} · {customer}
        </p>
        <p className="schedule-calendar-modal-meta">
          Reported {formatReportDate(report.date)}
          {reportedBy ? ` by ${reportedBy}` : ""}
        </p>

        <div className="inspector-report-stats">
          {contractor ? <StatRow label="Contractor" value={contractor} /> : null}
          <StatRow
            label="Conduit installed"
            value={
              report.conduit_footage != null && report.conduit_footage !== ""
                ? `${formatFootageNumber(Number(report.conduit_footage))} ft`
                : "—"
            }
          />
          <StatRow
            label="Fiber installed"
            value={
              report.fiber_footage != null && report.fiber_footage !== ""
                ? `${formatFootageNumber(Number(report.fiber_footage))} ft`
                : "—"
            }
          />
          <StatRow
            label="Proofing completed"
            value={
              report.proofing_footage != null && report.proofing_footage !== ""
                ? `${formatFootageNumber(Number(report.proofing_footage))} ft`
                : "—"
            }
          />
          <StatRow
            label="Vaults placed"
            value={
              report.vaults != null && report.vaults !== ""
                ? formatFootageNumber(Number(report.vaults))
                : "—"
            }
          />
          {notes ? (
            <div className="inspector-report-notes">
              <span className="inspector-report-stat-label">Notes</span>
              <p className="inspector-report-notes-text">{notes}</p>
            </div>
          ) : null}
        </div>

        <div className="schedule-calendar-modal-actions">
          <Link
            to={`/project/${projectRow.id}`}
            className="phase-schedule-project-link"
            onClick={onClose}
          >
            Open segment
          </Link>
          <button type="button" className="email-triggers-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
